/** Live SLA countdown chip for an Approval row.
 *
 *  Renders a 24-hour (or configured) countdown from `requested_at`.
 *  Colour graduates: green > 8h · amber ≤ 8h · red ≤ 1h · slate when
 *  breached.  Re-computes every 30 seconds so an operator sees the
 *  clock actually running without a page refresh.
 */
import { useEffect, useState } from "react";

const DEFAULT_SLA_HOURS = 24;

function _remaining(requestedAt, slaHours) {
  if (!requestedAt) return null;
  const start = new Date(requestedAt).getTime();
  if (Number.isNaN(start)) return null;
  const deadline = start + slaHours * 3600 * 1000;
  return deadline - Date.now();
}

function _fmt(ms) {
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  if (h >= 24) {
    const d = Math.floor(h / 24);
    const rh = h % 24;
    return `${d}d ${rh}h`;
  }
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function SlaCountdown({ requestedAt, slaHours = DEFAULT_SLA_HOURS,
                                              testId }) {
  const [ms, setMs] = useState(() => _remaining(requestedAt, slaHours));

  useEffect(() => {
    if (!requestedAt) return;
    const tick = () => setMs(_remaining(requestedAt, slaHours));
    tick();
    const int = setInterval(tick, 30_000);
    return () => clearInterval(int);
  }, [requestedAt, slaHours]);

  if (ms == null) {
    return (
      <span data-testid={testId}
             className="text-[10px] uppercase tracking-wider text-slate-400">
        —
      </span>
    );
  }

  let tone;
  let label;
  if (ms <= 0) {
    tone = "bg-slate-100 text-slate-700 border-slate-300";
    label = `Overdue · ${_fmt(ms)}`;
  } else if (ms <= 3600 * 1000) {           // ≤ 1 hour
    tone = "bg-red-50 text-red-700 border-red-300 animate-pulse";
    label = `${_fmt(ms)} left`;
  } else if (ms <= 8 * 3600 * 1000) {       // ≤ 8 hours
    tone = "bg-amber-50 text-amber-700 border-amber-300";
    label = `${_fmt(ms)} left`;
  } else {
    tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
    label = `${_fmt(ms)} left`;
  }

  return (
    <span data-testid={testId}
           title={`SLA ${slaHours}h · requested ${new Date(requestedAt).toLocaleString()}`}
           className={`inline-flex items-center text-[10px] font-medium tracking-wide uppercase px-1.5 py-0.5 rounded border ${tone}`}>
      {label}
    </span>
  );
}
